/* eslint-disable rxjs-x/finnish */
import { inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { EmptyFeatureResult, SignalStoreFeature, signalStoreFeature, withHooks, withMethods, withProps } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, Subject, merge, pipe, switchMap, take, takeUntil, tap } from 'rxjs';

import { websocketFailure } from '@core/stores/websocket/websocket.actions';

type MessageKey<Name extends string> = `${Name}Message$`;
type StartWebsocketMethodKey<Name extends string> = `${Name}StartWebsocket`;

type ResourceWebsocketProps<Name extends string, Message> = { [K in MessageKey<Name>]: Subject<Message> };
type ResourceWebsocketMethods<Name extends string> = { [K in StartWebsocketMethodKey<Name>]: () => void };

/**
 * Reusable signal-store feature for "sync a resource over a websocket topic, gated behind a
 * successful load, torn down on websocket failure". Given a `name`, the `{name}LoadStart$` /
 * `{name}LoadSuccess$` subjects produced by `withApiResource(name, ...)` for the SAME name, and a
 * `subscribe` function, produces:
 * - `{name}Message$` — a subject of arriving messages, for the consumer to tap and merge into its
 *   own domain state (this feature does not know how to merge — that's resource-specific)
 * - a `{name}StartWebsocket()` method that must be called explicitly to activate. Not auto-wired,
 *   so consumers that shouldn't open a socket (e.g. an aggregate/global scope of the same store
 *   class) simply never call it.
 *
 * Internally: waits for `{name}LoadStart$`, then the next `{name}LoadSuccess$`, then subscribes via
 * `subscribe(input, result)` and forwards messages until `websocketFailure` — at which point it goes
 * back to waiting for the next successful load (driven by a fresh `{name}LoadStart$`, e.g. from
 * `withReloadOnReconnect`). Built on `rxMethod`, so the subscription is automatically torn down when
 * the store is destroyed — no manual cleanup plumbing needed here.
 *
 * Depends on `withApiResource(name, ...)` being composed on the same store first. Since this feature
 * needs those subjects for the SAME name, wire it in via `withFeature`, which gives full,
 * contextually-inferred access to the already-composed store — avoids the type-inference issues that
 * come from trying to declare a feature's Input as an intersection of multiple prior generic features:
 *
 * ```ts
 * signalStoreFeature(
 *   withState<MyState>(initialState),
 *   withApiResource('thing', loader),
 *   withFeature((store) => withResourceWebsocket('thing', store.thingLoadStart$, store.thingLoadSuccess$, subscribeFn)),
 *   ...
 * )
 * ```
 *
 * `subscribe` runs later, inside an async pipeline — like `withApiResource`'s `loader`, it must not
 * call `inject()` itself. Resolve any services it needs inside the `withFeature((store) => {...})`
 * wrapper (synchronous, in context) and capture them by closure.
 *
 * `clear$` is a manual "stop everything" signal (e.g. `withCancellation('clear')` composed earlier
 * on the same store) for stores that aren't reliably destroyed when their owning screen is. It's
 * merged into the same `takeUntil` as `websocketFailure` — either one tears down the current socket
 * subscription, after which the watcher goes back to waiting for the next successful load. There's
 * no other local state here to reset.
 */
export function withResourceWebsocket<Name extends string, Input, T, Message>(
  name: Name,
  loadStart$: Observable<Input>,
  loadSuccess$: Observable<T>,
  subscribe: (input: Input, result: T) => Observable<Message>,
  clear$: Observable<void>
): SignalStoreFeature<
  EmptyFeatureResult,
  { state: object; props: ResourceWebsocketProps<Name, Message>; methods: ResourceWebsocketMethods<Name> }
> {
  const messageKey = `${name}Message$` as MessageKey<Name>;
  const startMethodKey = `${name}StartWebsocket` as StartWebsocketMethodKey<Name>;

  return signalStoreFeature(
    withProps(() => ({ [messageKey]: new Subject<Message>() }) as ResourceWebsocketProps<Name, Message>),
    withMethods((store) => {
      const props = store as unknown as ResourceWebsocketProps<Name, Message>;
      const actions$ = inject(Actions);
      let started = false;

      const watch = rxMethod<void>(
        pipe(
          switchMap(() =>
            loadStart$.pipe(
              switchMap((input) =>
                loadSuccess$.pipe(
                  take(1),
                  switchMap((result) =>
                    subscribe(input, result).pipe(
                      tap((message) => props[messageKey].next(message)),
                      takeUntil(merge(actions$.pipe(ofType(websocketFailure)), clear$))
                    )
                  )
                )
              )
            )
          )
        )
      );

      const startWebsocket = (): void => {
        if (started) {
          return;
        }
        started = true;
        watch(undefined);
      };

      return { [startMethodKey]: startWebsocket } as ResourceWebsocketMethods<Name>;
    }),
    withHooks({
      onDestroy(store) {
        (store as unknown as ResourceWebsocketProps<Name, Message>)[messageKey].complete();
      },
    })
  );
}
